import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Wrench } from "lucide-react";
import ShareSheet from "@/components/ShareSheet";
import { resolveImageUrl } from "@/lib/api";
import { photoList } from "@/components/ImageSlider";

export const serviceUrl = (svc) => `${window.location.origin}/service/${svc.id}`;
// Only live, approved services may be shared publicly.
export const isShareableService = (svc) => !!svc && svc.active !== false && !["pending", "rejected"].includes(svc.review_status);
const priceText = (svc) => (svc.price_from == null || svc.price_from === "" ? "" : `₹${svc.price_from}${svc.unit ? ` / ${svc.unit}` : ""}`);

function ServiceCard({ svc, url }) {
  const photo = photoList(svc)[0];
  const price = priceText(svc);
  const by = svc.provider_name || svc.provider || svc.shop_name || "";
  return (
    <div className="relative bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-3 sm:p-4 pb-4">
      <div className="flex items-baseline gap-1.5 mb-3">
        <span className="font-display text-xl font-black tracking-tight text-emerald-800">VerB</span>
        <span className="text-[9px] tracking-[0.22em] text-slate-500 font-semibold">Vertual Bazaar</span>
      </div>
      {photo ? (
        <div className="rounded-xl overflow-hidden bg-slate-100 aspect-[16/9] border-2 border-white shadow">
          <img src={resolveImageUrl(photo)} alt={svc.name} crossOrigin="anonymous" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="service-card-photo" />
        </div>
      ) : (
        <div className="rounded-xl bg-gradient-to-br from-emerald-100 to-amber-50 h-16 flex items-center justify-center" data-testid="service-card-no-photo"><Wrench className="w-7 h-7 text-emerald-600" /></div>
      )}
      <div className="mt-3 min-w-0">
        <div className="font-display text-lg sm:text-xl font-black text-slate-900 leading-tight break-words" data-testid="service-card-name">{svc.name}</div>
        {svc.category && <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium mt-0.5 break-words">{svc.category}</div>}
        {by && <div className="text-[11px] text-slate-600 mt-1 break-words" data-testid="service-card-provider">{by}</div>}
        {price && <div className="mt-1.5 inline-block rounded-lg bg-emerald-700 text-white text-sm font-bold px-2.5 py-1 break-words max-w-full" data-testid="service-card-price">{price}</div>}
      </div>
      <div className="mt-3 flex items-center gap-3 rounded-xl bg-white border border-emerald-100 p-2.5 sm:p-3">
        <div className="bg-white p-1 rounded-lg border border-slate-100 flex-shrink-0" data-testid="service-share-qr">
          <QRCodeSVG value={url} size={88} bgColor="#ffffff" fgColor="#065f46" level="M" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-900 leading-tight">Scan to view service</div>
          <div className="text-[11px] text-slate-600 leading-tight mt-0.5">Open with any camera</div>
          <div className="text-[10px] text-slate-500 mt-1 break-all line-clamp-2" data-testid="service-card-url">{url}</div>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-emerald-100 flex flex-wrap items-center justify-between gap-x-2 text-[10px] text-slate-500">
        <span>verb.app · Local Vertual Bazaar</span>
        <span className="text-emerald-800">Shop with your neighbourhood</span>
      </div>
    </div>
  );
}

export default function ServiceShareSheet({ open, onClose, svc }) {
  if (!open || !svc) return null;
  const url = serviceUrl(svc);
  const price = priceText(svc);
  return (
    <ShareSheet open={open} onClose={onClose} heading="Share this service" noun="service"
      shopName={(svc.name || "").trim()} tagline={[price, svc.provider_name || svc.provider || svc.shop_name].filter(Boolean).join(" · ")}
      shareUrl={url} profileUrl={url} card={<ServiceCard svc={svc} url={url} />} />
  );
}
